/**
 * One granted price card (`/rate-cards/:id`, TMMAPI-0131; one card set by the platform, TMMRSL-0075,
 * MM-D-136). The header carries the name, the Adopted chip, the currency and Adopt…; below it two
 * tabs in the URL (`?tab=worksheet|prices`, the worksheet the default and omitted, TMM-B6): the
 * read-only margin worksheet and the read-only prices. OWNER and ADMIN adopt the card for new sales;
 * existing customers keep their price. Adopt is blocked, with its reason visible and as the button's
 * accessible description, only while there is no agreement. Every command reloads from the server.
 */
import { useCallback, useId, useState } from 'react';
import { Box, Button, Skeleton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { rateCardsApi, type GrantedCard } from '@/api/rateCards';
import { useCanWrite } from '@/auth/authHooks';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageBreadcrumbs, PageHeader, type Breadcrumb } from '@/components/PageHeader';
import { useActionError } from '@/components/useActionError';
import { useRemote } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { enumParam, useUrlState } from '@/components/useUrlState';
import { StatusChip } from '@/components/status/StatusChip';
import { NotFound } from '@/routes/NotFound';
import { RateCardPrices } from './RateCardPrices';
import { RateCardWorksheet } from './RateCardWorksheet';
import { rateCardErrorMessage } from './rateCardErrors';

interface LocationState {
  listSearch?: unknown;
}

const TABS = ['worksheet', 'prices'] as const;
const TAB_SCHEMA = { tab: enumParam(TABS, 'worksheet') };

export function RateCardScreen() {
  const { id } = useParams();
  if (!id || !/^\d+$/.test(id)) return <NotFound />;
  return <RateCardPage key={id} id={Number(id)} />;
}

function RateCardPage({ id }: { id: number }) {
  const { t } = useTranslation();
  const location = useLocation();
  const canWrite = useCanWrite();
  const showToast = useToast();
  const describe = useActionError(rateCardErrorMessage);
  const state = (location.state ?? null) as LocationState | null;
  const listSearch = typeof state?.listSearch === 'string' ? state.listSearch : '';
  const loadCard = useCallback(() => rateCardsApi.getGrantedCard(id), [id]);
  const remote = useRemote(loadCard);
  const [confirming, setConfirming] = useState(false);
  const card = remote.data;

  const parent: Breadcrumb = {
    label: t('pages.rateCards.title'),
    to: `/rate-cards${listSearch}`,
  };

  if (
    remote.status === 'error' &&
    remote.error instanceof ApiError &&
    remote.error.code === 'NOT_FOUND'
  ) {
    return (
      <Box>
        <PageHeader
          id="rateCard"
          title={t('rateCards.notFound')}
          subtitle=""
          breadcrumbs={[parent, { label: t('rateCards.notFound') }]}
        />
      </Box>
    );
  }

  return (
    <Box>
      {!card ? <PageBreadcrumbs items={[parent, { label: t('pages.rateCard.kicker') }]} /> : null}
      {remote.status === 'error' ? (
        <ApiErrorState what={t('errors.what.rateCard')} error={remote.error} onRetry={remote.reload} />
      ) : null}
      {remote.status === 'loading' && !card ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={220} />
        </Box>
      ) : null}
      {card ? (
        <CardDetail
          card={card}
          parent={parent}
          canWrite={canWrite}
          confirming={confirming}
          onAsk={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onAdopt={async () => {
            try {
              const adopted = await rateCardsApi.adoptCard(id);
              showToast(t('rateCards.adopt.toast', { name: adopted.name || card.name }));
              remote.reload();
            } catch (err) {
              if (describe(err).code === 'AGREEMENT_REQUIRED') remote.reload();
              throw err;
            }
          }}
          errorMessage={(err) => describe(err).message}
        />
      ) : null}
    </Box>
  );
}

/** Why Adopt is blocked, or `null`. Without an agreement tmm-api sends no margin and no example. */
function adoptBlockReason(card: GrantedCard, t: (key: string) => string): string | null {
  if (card.marginMinor === null && !card.hasExample) return t('rateCards.adopt.needsAgreement');
  return null;
}

function CardDetail({
  card,
  parent,
  canWrite,
  confirming,
  onAsk,
  onCancel,
  onAdopt,
  errorMessage,
}: {
  card: GrantedCard;
  parent: Breadcrumb;
  canWrite: boolean;
  confirming: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onAdopt: () => Promise<void>;
  errorMessage: (err: unknown) => string;
}) {
  const { t } = useTranslation();
  const ids = useId();
  const url = useUrlState(TAB_SCHEMA);
  const name = card.name || t('rateCard.panel.unknown');
  const blocked = adoptBlockReason(card, t);
  const showAdopt = canWrite && !card.adopted;
  const reasonId = `${ids}-adopt-reason`;
  const tab = url.values.tab;
  const tabId = (value: (typeof TABS)[number]) => `${ids}-tab-${value}`;
  const panelId = `${ids}-panel`;

  return (
    <>
      <PageHeader
        id="rateCard"
        title={name}
        subtitle={card.currency ? t('rateCards.inCurrency', { currency: card.currency }) : ''}
        breadcrumbs={[parent, { label: name }]}
        actions={
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {card.adopted ? <StatusChip domain="rateCard" status="ADOPTED" /> : null}
            {showAdopt ? (
              blocked ? (
                <Tooltip title={blocked} describeChild>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    {/* Blocked, not natively disabled: it keeps focus, so the keyboard reaches its
                        tooltip; with no handler, Enter and click do nothing (TMMRSL-0072). */}
                    <Button
                      variant="contained"
                      aria-disabled
                      className="Mui-disabled"
                      aria-describedby={reasonId}
                    >
                      {t('rateCards.adopt.label')}
                    </Button>
                  </Box>
                </Tooltip>
              ) : (
                <Button variant="contained" onClick={onAsk}>
                  {t('rateCards.adopt.label')}
                </Button>
              )
            ) : null}
          </Stack>
        }
      />
      {showAdopt && blocked ? (
        // The blocked Adopt's reason, visible and its accessible description (TMMRSL-0074 follow-up).
        <Typography id={reasonId} variant="body2" sx={{ color: 'text.secondary', mt: -1, mb: 2 }}>
          {blocked}
        </Typography>
      ) : null}
      <Tabs
        value={tab}
        onChange={(_, value: (typeof TABS)[number]) => url.set({ tab: value })}
        aria-label={t('rateCards.tabs.label')}
        sx={{ mb: 2 }}
      >
        <Tab
          value="worksheet"
          label={t('rateCards.tabs.worksheet')}
          id={tabId('worksheet')}
          aria-controls={panelId}
        />
        <Tab
          value="prices"
          label={t('rateCards.tabs.prices')}
          id={tabId('prices')}
          aria-controls={panelId}
        />
      </Tabs>
      <Box role="tabpanel" id={panelId} aria-labelledby={tabId(tab)}>
        {tab === 'prices' ? <RateCardPrices card={card} /> : <RateCardWorksheet card={card} />}
      </Box>
      <ConfirmDialog
        open={confirming}
        title={t('rateCards.adopt.title', { name })}
        consequence={t('rateCards.adopt.consequence')}
        confirmLabel={t('rateCards.adopt.confirm')}
        onConfirm={onAdopt}
        onCancel={onCancel}
        errorMessage={errorMessage}
      />
    </>
  );
}
